import {
  getBooleanFromLocalStorage,
  getFloatFromLocalStorage,
  getIntegerFromLocalStorage,
  getStringFromLocalStorage,
  writeToLocalStorage,
} from '@/utils/local-storage/local-storage'
import {
  HLS_AUDIO_LANGUAGE,
  HLS_MUTED,
  HLS_PLAYBACK_QUALITY,
  HLS_PLAYBACK_SPEED,
  HLS_SUBTITLE_LANGUAGE,
  HLS_VOLUME,
} from '@/utils/local-storage/local-storage-keys'

export interface PlaybackPreferences {
  /** Quality label (e.g., "720p", "Auto"). Stored as label, not index, since indices vary by video. */
  qualityLabel: string | undefined
  /** Playback speed (e.g., 1.5). */
  speed: number | undefined
  /** Preferred audio language code (e.g., "en", "es"). */
  audioLanguage: string | undefined
  /** Preferred subtitle language code, or "off" for disabled. */
  subtitleLanguage: string | undefined
  /** Volume level 0–100. */
  volume: number | undefined
  /** Muted state. */
  muted: boolean | undefined
}

export interface PlaybackPreferencesActions {
  saveQuality: (label: string) => void
  saveSpeed: (speed: number) => void
  saveAudioLanguage: (lang: string) => void
  saveSubtitleLanguage: (lang: string) => void
  saveVolume: (volume: number) => void
  saveMuted: (muted: boolean) => void
}

/**
 * Reads and writes playback preferences from/to local storage.
 *
 * Preferences are only written when the user explicitly interacts with a setting.
 * If a stored preference is not available in the current video (e.g., stored "Spanish"
 * audio but the video only has English), the caller should fall back to the default
 * without clearing or overwriting the stored preference.
 */
export function usePlaybackPreferences(): PlaybackPreferences & PlaybackPreferencesActions {
  return {
    qualityLabel: getStringFromLocalStorage(HLS_PLAYBACK_QUALITY),
    speed: getFloatFromLocalStorage(HLS_PLAYBACK_SPEED),
    audioLanguage: getStringFromLocalStorage(HLS_AUDIO_LANGUAGE),
    subtitleLanguage: getStringFromLocalStorage(HLS_SUBTITLE_LANGUAGE),
    volume: getIntegerFromLocalStorage(HLS_VOLUME),
    muted: getBooleanFromLocalStorage(HLS_MUTED),

    saveQuality: (label: string) => writeToLocalStorage(HLS_PLAYBACK_QUALITY, label),
    saveSpeed: (speed: number) => writeToLocalStorage(HLS_PLAYBACK_SPEED, speed),
    saveAudioLanguage: (lang: string) => writeToLocalStorage(HLS_AUDIO_LANGUAGE, lang),
    saveSubtitleLanguage: (lang: string) => writeToLocalStorage(HLS_SUBTITLE_LANGUAGE, lang),
    saveVolume: (volume: number) => writeToLocalStorage(HLS_VOLUME, volume),
    saveMuted: (muted: boolean) => writeToLocalStorage(HLS_MUTED, muted),
  }
}
